export default {
    language: {
        title: 'Language',
        description: 'The language you see the app in. Changing it does not translate what you have already saved.',
        saved: 'Saved.',
        failed: 'Could not save the language.',
    },
    gender: {
        label: 'Gender',
        female: 'Woman',
        male: 'Man',
    },
    currencies: {
        PLN: 'Polish złoty (zł)',
        EUR: 'Euro (€)',
        GBP: 'British pound (£)',
        USD: 'US dollar ($)',
        CHF: 'Swiss franc (CHF)',
        CZK: 'Czech koruna (Kč)',
        SEK: 'Swedish krona (kr)',
        NOK: 'Norwegian krone (kr)',
        DKK: 'Danish krone (kr)',
        CAD: 'Canadian dollar ($)',
        AUD: 'Australian dollar ($)',
    },
    deleteAccount: {
        title: 'Delete account',
        description: 'Your account and all your data disappear immediately and for good.',
        open: 'Delete account…',
        confirmTitle: 'Delete your account for good?',
        whatGoes: 'We’ll immediately delete your account and everything saved in it: the planner (tasks, events, notes, sleep, workouts, day entries), meals and products, shopping lists, finances and Wallet, projects, settings and AI history. This cannot be undone.',
        googleNote: 'Access to Google Calendar will be revoked. Events in Google Calendar itself stay.',
        confirmPassword: 'Confirm with your password',
        orGoogle: 'or sign in again with Google',
        confirmGoogle: 'Confirm by signing in again with Google',
        wrongPassword: 'Incorrect password.',
        googleMismatch: 'Sign in with the Google account connected to Rountine and try again straight away.',
        failed: 'Couldn’t delete the account. Try again or write to kontakt{\'@\'}rountine.com.',
    },
    profileSetup: {
        title: 'Complete your details',
        intro: 'Your account is ready. A few details so the app suits you better — you can change everything later in Settings.',
        genderHint: '— this is how we’ll address you',
        later: 'Later',
    },
    googleCalendar: {
        reasons: {
            denied: 'Permission wasn’t granted in Google.',
            scope: 'Sync won’t work without access to your calendar — tick it when connecting.',
            state: 'The connection link has expired — please try again.',
            google: 'Google didn’t respond properly — please try again.',
        },
        connected: 'Connected to Google Calendar.',
        connectFailed: 'Couldn’t connect to Google.',
        startFailed: 'Couldn’t start connecting to Google.',
        saveFailed: 'Couldn’t save the calendar settings.',
        syncFailed: 'Sync failed.',
        synced: 'Synced.',
        syncedChanged: 'Synced — events changed: {n}.',
        syncError: 'Couldn’t sync.',
        disconnected: 'Google account disconnected. Your events stay in the app.',
        disconnectFailed: 'Couldn’t disconnect the account.',
        primary: '{name} (primary)',
        notYet: 'not yet',
        unavailable: 'This API doesn’t support Google Calendar yet.',
        connection: 'Connection',
        connectionNotConfiguredDescription: 'Syncing events with Google Calendar.',
        notConfigured: 'Google sync isn’t set up on the server yet.',
        connectionDescription: 'Events from the app can go to your Google Calendar, and those from Google into the planner.',
        redirecting: 'Redirecting…',
        connect: 'Connect with Google',
        account: 'Google account',
        accountDescription: 'The account whose calendar we sync events with.',
        reauth: 'Google has revoked the app’s access — sync has stopped. Connect your account again.',
        reconnect: 'Connect again',
        disconnect: 'Disconnect account',
        calendar: 'Calendar',
        calendarDescription: 'Changing the calendar unlinks events from the previous one (they stay in the app) and fetches the new one.',
        searchCalendar: 'Search calendars…',
        sync: 'Sync',
        syncDescription: 'You switch each direction on separately.',
        export: 'Send events from the app to Google',
        exportHint: 'Adding, changing and deleting an event goes straight to the calendar. Once switched on, we’ll also send events from today onwards.',
        import: 'Fetch events from Google into the app',
        importHint: 'Every few minutes and when you open the planner. If you switch it off, fetched events stay in the app.',
        status: 'Status',
        statusDescription: 'Sync runs on its own every few minutes.',
        lastSync: 'Last sync: {when}',
        lastError: 'Last error: {error}',
        wait: 'One moment…',
        syncNow: 'Sync now',
        disconnectTitle: 'Disconnect your Google account?',
        disconnectMessage: 'Sync will stop working. Your events stay in the app, and nothing will be deleted in Google.',
        disconnectConfirm: 'Disconnect',
    },
    restoreDefaults: 'Restore defaults',
    tiles: {
        saveFailed: 'Couldn’t save the tiles.',
        onScreen: 'On screen · {count} of {max} — the first tile is highlighted.',
        up: 'Move up: {name}',
        down: 'Move down: {name}',
        remove: 'Remove from screen: {name}',
        available: 'Available',
        max: 'At most {max} tiles — remove one to add this one',
        add: 'Add: {name}',
    },
    profile: {
        account: 'Account',
        accountDescription: 'The address you sign in with.',
        logout: 'Sign out',
        googleTitle: 'Sign in with Google',
        googleDescription: 'Sign in to your account with Google instead of a password. It can be a Google account with a different address from the one above.',
        googleConnectedAs: 'Connected: {email}',
        googleDisconnect: 'Disconnect',
        noPassword: 'This account has no password — Google is the only way to sign in. To disconnect Google, {link}.',
        setPasswordFirst: 'set a password first',
        googleConnected: 'Google account connected — you can sign in with it.',
        googleTaken: 'This Google account is already connected to another Rountine account.',
        googleConnectFailed: 'Couldn’t connect the Google account.',
        googleDisconnected: 'Google account disconnected.',
        googleDisconnectFailed: 'Couldn’t disconnect the Google account.',
        genderDescription: 'How the app addresses you in its messages.',
        genderFailed: 'Couldn’t save your gender.',
        assistant: 'The assistant and you',
        assistantDescription: 'What your assistant is called, which voice it speaks with and how it should address you.',
        assistantName: 'Assistant’s name',
        assistantNamePlaceholder: 'Assistant',
        userName: 'What to call you',
        userNamePlaceholder: 'e.g. Paul',
        userNameHint: 'The assistant will call you by your name — sparingly',
        namesFailed: 'Couldn’t save the names.',
        currency: 'Currency',
        currencyDescription: 'All amounts — in Wallet, Finances, food and shopping prices. Changing it converts nothing, it only changes how amounts are shown.',
        currencyFailed: 'Couldn’t save the currency.',
        body: 'Body',
        bodyDescription: 'Weight and height — for calculations in the planner.',
        weight: 'Weight',
        height: 'Height',
        saveFailed: 'Couldn’t save your profile.',
    },
    app: {
        mode: 'Launch mode',
        modeDescription: 'How Rountine runs on this device.',
        standalone: 'App installed on your phone’s home screen.',
        browserTab: 'Browser tab. Quick start and icon shortcuts only work in the app installed from the browser menu (“Install app”).',
        phoneDescription: 'Add Rountine to your phone’s home screen — it opens like an app, without the browser bar.',
        android: 'open app.rountine.com in Chrome, menu ⋮ → “Install app” (or “Add to Home screen”).',
        iphone: 'open app.rountine.com in Safari, “Share” → “Add to Home Screen”.',
        googlePlay: 'The Android app is on its way to Google Play.',
        phoneDone: 'Rountine is already on your phone.',
        markPhone: 'I already have it on my phone',
        launcherDescription: 'Tiles with your most-used places instead of the assistant conversation — on launch and when you come back after at least 15 minutes.',
        launcher: 'Show quick start when opening',
        preview: 'Preview',
        tiles: 'Quick start tiles',
        tilesDescription: 'Which places are on the quick start screen and in what order. Saved to your account — the same on every device.',
        voice: 'Assistant voice',
        voiceDescription: 'Whether the assistant reads its replies out loud on this device.',
        voiceModes: {
            spoken: 'When I speak to it',
            always: 'Always',
            never: 'Never',
        },
        voiceKind: 'Which voice',
        voices: {
            female: 'Female',
            male: 'Male',
        },
        voiceListen: 'Listen',
        voiceSample: {
            female: 'Hi! Noted: Biedronka, one hundred zloty. Do you have the receipt?',
            male: 'Hi! Noted: Biedronka, one hundred zloty. Do you have the receipt?',
        },
        hints: 'Explanations',
        hintsDescription: '“What’s this?” pills next to sections in every module. The guide under “Help” works without them.',
        hintsToggle: 'Show “What’s this?” explanations',
    },
    wholeAccount: 'for the whole account',
    menu: {
        saveFailed: 'Couldn’t save the menu.',
        pinned: 'Pinned to the menu',
        pinnedDescription: 'They sit in the sidebar on a computer below Assistant and Planner, and the first two also in the bottom bar on a phone. Pin with the pin icon on a tile in the “All” panel.',
        pinnedEmpty: 'Nothing is pinned — every module is under “All”.',
        onPhone: 'also on phone',
        unpin: 'Unpin “{name}”',
        openAll: 'Open the “All” panel',
        resetPins: 'Restore default pins',
        modules: 'Modules',
        modulesDescription: 'Hide what you don’t use and set the order within each group. You can still find a hidden module with search (⌘K / Ctrl+K).',
        alwaysVisible: 'home page — always visible',
        moveUp: 'Move “{name}” up',
        moveDown: 'Move “{name}” down',
        reset: 'Restore default menu',
        beta: 'New module versions',
        betaDescription: 'Wallet is being built alongside Finances — it has its own data and may still change.',
        betaToggle: 'Show new module versions — in the menu, search and quick start tiles',
    },
    topBar: {
        saveFailed: 'Couldn’t save the bar layout.',
        buttons: 'Buttons',
        buttonsDescription: 'Switch on what you use and set the order from left to right. The same layout shows on your phone and computer.',
        empty: 'Empty bar',
        earlierTitle: 'Earlier — further left',
        earlier: 'Move “{name}” earlier',
        laterTitle: 'Later — further right',
        later: 'Move “{name}” later',
        searchHint: 'Search also works without the button: ⌘K / Ctrl+K.',
        reset: 'Restore default layout',
    },
    goals: {
        fields: {
            calories: 'Calories',
            protein: 'Protein',
            carbs: 'Carbohydrates',
            fat: 'Fat',
            fiber: 'Fibre',
            sugar: 'Sugar',
            saturated_fat: 'Saturated fat',
            salt: 'Salt',
        },
        lists: {
            allergies: { label: 'Allergies and intolerances', placeholder: 'e.g. nuts, lactose', hint: 'AI will never use them.' },
            avoid: { label: 'I don’t eat', placeholder: 'e.g. liver, dill' },
            likes: { label: 'I like', placeholder: 'e.g. Asian cuisine, grains' },
            equipment: { label: 'Kitchen equipment', placeholder: 'e.g. oven, blender, air fryer' },
        },
        activity: {
            low: { label: 'Sedentary', hint: 'desk job, no workouts' },
            light: { label: 'Light', hint: 'walks, 1–2 workouts a week' },
            moderate: { label: 'Moderate', hint: '3–4 workouts a week' },
            high: { label: 'High', hint: 'workouts almost every day' },
            very_high: { label: 'Very high', hint: 'physical work and workouts' },
        },
        aims: {
            cut: 'Lose weight',
            keep: 'Maintain weight',
            gain: 'Gain weight / muscle',
        },
        missing: {
            sex: 'gender',
            birth_year: 'year of birth',
            height: 'height',
            weight: 'weight',
            activity: 'activity',
            aim: 'goal',
        },
        missingPrompt: 'Please enter your {fields}.',
        calcFailed: 'Couldn’t calculate.',
        saveFailed: 'Couldn’t save.',
        daily: 'Daily goals',
        dailyDescription: 'One set for the whole app: the balance in Food, bars in the Planner, AI suggestions. Empty field = no goal.',
        calculated: 'Calculated — check the values below and save.',
        calcPrompt: 'Not sure how much? I’ll work it out from your details.',
        calcOpen: 'Work it out for me',
        calc: 'Calculate',
        birthYear: 'Year of birth',
        aim: 'Goal',
        activityLabel: 'Activity',
        prefs: 'Food preferences',
        prefsDescription: 'Set them once — every AI suggestion takes them into account: recipes in chat, planning days, meal analysis and quick entry.',
        removeItem: 'Remove: {item}',
        cookingTime: 'I usually have for cooking',
    },
    ai: {
        features: {
            entry_split: 'Recording — splitting into entries',
            task_extraction: 'Recording — task',
            event_extraction: 'Recording — event',
            sleep_extraction: 'Recording — sleep',
            meal_extraction: 'Recording — meal',
            training_extraction: 'Recording — workout',
            transcription: 'Speech recognition',
            shopping_dictation: 'Shopping dictation',
            recipe_import: 'Recipe import',
            receipt_analysis: 'Receipt from a photo',
            product_macros: 'Product macros from AI',
            note_extraction: 'Recording — note',
            meal_analysis: 'Meal analysis',
        },
        month: 'Month',
        limitTitle: 'AI limit',
        limitUsed: '{n}% of the monthly AI limit used',
        limitRenews: 'renews on {date}',
        limitReached: 'Limit used up — AI features return after it renews.',
        cost: 'Cost',
        calls: 'Calls',
        errors: '{n} error | {n} errors',
        input: 'Input',
        inputTokens: 'tokens, of which cached {n}',
        output: 'Output',
        outputTokens: 'tokens, of which “thinking” {n}',
        avgTime: 'Average time',
        allTime: 'All time: {calls} call, {cost}. Prices in USD according to OpenAI’s price list at the time of the call. | All time: {calls} calls, {cost}. Prices in USD according to OpenAI’s price list at the time of the call.',
        none: 'There were no AI calls this month.',
        byFeature: 'By feature',
        byModel: 'By model',
        latest: '— latest {n}',
        inputDetail: '{tokens} (cache {cached})',
        outputDetail: '{tokens} (“thinking” {reasoning})',
        recording: 'Recording',
        error: 'Error: {error}',
        contentFailed: 'Couldn’t fetch the content.',
        inputSent: 'Input (sent)',
        outputReceived: 'Output (response)',
        copy: 'Copy',
    },
};
